<template>
  <div>
    <!-- Label -->
    <label v-if="label" class="label-text-black" :for="inputId">
      {{ label }}
      <span v-if="required" class="required-sign">*</span>
    </label>

    <!-- Upload Container -->
    <div class="attachment-wrapper">
      <div
        class="attachment-container mt-2"
        :class="[
          {
            dragover: isDragging,
            disabled: loading || disabled,
            'has-error': showSizesError || showExtensionsError || error,
          },
          customClasses
        ]"
        @dragover.prevent="handleDragOver"
        @dragleave="handleDragLeave"
        @drop.prevent="handleDrop">
        <label class="image-card" :for="inputId">
          <p>{{ uploadMessage || $t("file-message") }}</p>
          <div class="upload-image-border">
            <img :src="uploadIcon" alt="upload image" />
          </div>
        </label>

        <input
          :id="inputId"
          ref="fileInput"
          type="file"
          :multiple="multiple"
          :accept="accept"
          class="d-none"
          :disabled="loading || disabled"
          @change="handleFilesUpload" />

        <div
          v-if="
            (multiple && selectedFiles.length > 0) ||
            (!multiple && selectedFiles)
          "
          class="scrollable-container">
          <div class="selected-files mt-3">
            <h4 class="label-text-black mb-2">
              {{ filesTitle || "Selected Files:" }}
            </h4>
            <div class="files-list">
              <template v-if="multiple">
                <div
                  v-for="(file, index) in selectedFiles"
                  :key="index"
                  class="file-item">
                  <div class="file-info">
                    <img :src="imageIcon" alt="attachment" />

                    <span class="file-name">{{ file.name }}</span>
                    <span class="file-size"
                      >({{ formatFileSize(file.size) }})</span
                    >
                  </div>
                  <img
                    @click="removeFile(index)"
                    :src="deleteIcon"
                    alt="delete"
                    class="cursor-pointer" />
                </div>
              </template>

              <template v-else>
                <div class="file-item">
                  <div class="file-info">
                    <img :src="imageIcon" alt="attachment" />

                    <span class="file-name">{{ selectedFiles.name }}</span>
                    <span class="file-size"
                      >({{ formatFileSize(selectedFiles.size) }})</span
                    >
                  </div>
                  <img
                    @click="removeFile(0)"
                    :src="deleteIcon"
                    alt="delete"
                    class="cursor-pointer" />
                </div>
              </template>
            </div>
          </div>
        </div>
      </div>
      <div class="hint-wrapper">
        <div class="bottom-content">
          <p class="hint-message">
            {{ hintMessage || $t("image-extensions-maximum-5MB") }}
          </p>
          <p v-if="showSizesError" class="error-message">
            {{ $t("attached-file-size-exceeds-the-maximum-allowed-size-custom", { size: 5 }) || "File size exceeds maximum limit" }}
          </p>
          <p v-if="showExtensionsError" class="error-message">
            {{ $t("only-extensions-are-accepted-receipt-voucher") || "File type not supported" }}
          </p>
          <p v-if="error" class="error-message">
            {{ error }}
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { extcheck, sizecheck } from "@/lib/helpers";

export default {
  name: "FileUpload",
  props: {
    label: {
      type: String,
      default: "",
    },
    required: {
      type: Boolean,
      default: false,
    },
    multiple: {
      type: Boolean,
      default: false,
    },
    accept: {
      type: String,
      default: "image/*,.pdf",
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    loading: {
      type: Boolean,
      default: false,
    },

    uploadMessage: {
      type: String,
      default: "",
    },
    hintMessage: {
      type: String,
      default: "",
    },
    loadingMessage: {
      type: String,
      default: "",
    },
    filesTitle: {
      type: String,
      default: "",
    },
    maxFileSize: {
      type: Number,
      default: 5 * 1024 * 1024, // 5MB in bytes
    },
    maxFiles: {
      type: Number,
      default: 10,
    },
    value: {
      type: Array | Object,
    },
    error: {
      type: String,
      default: "",
    },
    customClasses: {
      type: [String, Array, Object],
      default: "",
    },
  },

  data() {
    return {
      isDragging: false,
      dragCounter: 0,
      selectedFiles: this.initializeSelectedFiles(),
      showSizesError: false,
      showExtensionsError: false,
      inputId: `file-input-${this._uid}`, // Unique ID for each component instance
    };
  },
  computed: {
    uploadIcon() {
      return require("@/assets/images/icons/upload-icon.svg");
    },
    imageIcon() {
      return require("@/assets/images/icons/image-icon.svg");
    },

    deleteIcon() {
      return require("@/assets/images/icons/trash-icon.svg");
    },
  },
  watch: {
    value: {
      handler() {
        this.selectedFiles = this.initializeSelectedFiles();
        this.clearErrors(); 
      },
      deep: true,
    },
  },

  methods: {
    initializeSelectedFiles() {
      if (!this.value) {
        return this.multiple ? [] : null;
      }

      if (this.multiple) {
        return Array.isArray(this.value) ? [...this.value] : [];
      } else {
        return Array.isArray(this.value) ? this.value[0] : this.value;
      }
    },
    validateFile(file) {
      // Check file size
      if (!sizecheck(file, 5120)) {
        return {
          isValid: false,
          type: "size",
          errorMessage:
            this.$t(
              "attached-file-size-exceeds-the-maximum-allowed-size-custom",
              {
                size: 5,
              }
            ) || "File size exceeds maximum limit",
        };
      }

      // Check file extension
      if (!extcheck(file, ["png", "jpg", "jpeg", "svg", "pdf"])) {
        return {
          isValid: false,
          type: "extension",
          errorMessage:
            this.$t("only-extensions-are-accepted-receipt-voucher") ||
            "File type not supported",
        };
      }
      
      return { isValid: true, errorMessage: "" };
    },
    handleDragOver(e) {
      e.preventDefault();
      this.dragCounter++;
      this.isDragging = true;
      this.clearErrors();
    },

    handleDragLeave(e) {
      e.preventDefault();
      this.dragCounter--;
      if (this.dragCounter === 0) {
        this.isDragging = false;
      }
    },

    handleDrop(e) {
      e.preventDefault();
      this.isDragging = false;
      this.dragCounter = 0;

      if (this.loading || this.disabled) {
        return;
      }

      const files = Array.from(e.dataTransfer.files);
      this.processFiles(files);
    },

    handleFilesUpload(e) {
      const files = Array.from(e.target.files);
      this.processFiles(files);
        // Reset file input so the same file can be selected again
      e.target.value = null;
    },

    async processFiles(files) {
      this.clearErrors();

      if (!files.length) {
        return;
      }
      for (const file of files) {
        const { isValid, errorMessage, type } = this.validateFile(file);

        if (!isValid) {
          // Update local error state based on the validation result
          if (type === "size") {
            this.showSizesError = true;
          } else if (type === "extension") {
            this.showExtensionsError = true;
          }
          this.$emit("validation-error", errorMessage);
          return;
        }
      }

      if (this.multiple) {
        if (this.selectedFiles.length + files.length > this.maxFiles) {
          const errorMsg =
            this.$t("maximum-files-exceeded", { max: this.maxFiles }) ||
            `Maximum ${this.maxFiles} files allowed`;
          this.$emit("validation-error", errorMsg);
          return;
        }

        // Add files to existing selection
        this.selectedFiles = [...this.selectedFiles, ...files];
      } else {
        this.selectedFiles = files[0];
      }
      this.$emit("validation-error", "");
      this.$emit("upload", this.selectedFiles);
    },

    removeFile(index) {
      if (this.multiple) {
        // For multiple files, remove the file at the specified index
        this.selectedFiles.splice(index, 1);
      } else {
        // For single file, clear the selected file
        this.selectedFiles = null;
      }

      this.$emit("file-removed", index);
      this.$emit("upload", this.selectedFiles);
    },

    clearErrors() {
      this.showSizesError = false;
      this.showExtensionsError = false;
      this.$emit("validation-error", "");
    },

    resetComponent() {
      this.selectedFiles = this.multiple ? [] : null;
      this.clearErrors();
      this.isDragging = false;
      this.dragCounter = 0;
    },
  },
};
</script>

<style scoped lang="scss">
.attachment-wrapper {
  .attachment-container {
    margin-top: 8px;
    padding: 0 !important;
    border: 2px dashed transparent;
    transition: border-color 0.3s ease;
    background: var(--color-128);

    &.disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }

    &.has-error {
      border: 2px dashed var(--color-47);
    }
  }

  .scrollable-container {
    max-height: 330px;
    overflow-y: auto;
    padding: 0 16px 16px;
  }

  .image-card {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 16px;
    border-radius: 4px;
    border: 1px dashed var(--color-119);
    background: var(--color-01);
    cursor: pointer;
    margin: 16px;

    p {
      font-size: 14px;
      line-height: 20px;
      color: var(--color-61);
      font-weight: 400;
      margin: 0;
      text-align: center;
    }

    .upload-image-border {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 35px;
      height: 35px;
      background: var(--color-04);
      border-radius: 50%;

      img {
        width: 18px;
        height: 18px;
        object-fit: contain;
      }
    }
  }

  .hint-wrapper {
    margin-top: 12px;
    .bottom-content {
      display: flex;
      flex-direction: column;
      gap: 8px;
    }

    .hint-message {
      font-size: 12px;
      line-height: 16px;
      color: var(--color-66);
      font-weight: 400;
      letter-spacing: 0.4px;
      margin: 0;
    }

    .error-message {
      color: var(--color-47);
      font-size: 12px;
      line-height: 16px;
      font-weight: 400;
      letter-spacing: 0.4px;
      margin: 0;
    }
  }
}

.selected-files {
  .files-list {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .file-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 12px 12px;
    background-color: var(--color-01);
    border: 1px solid var(--color-119);
    border-radius: 6px;

    .file-info {
      display: flex;
      align-items: center;
      gap: 8px;
      flex: 1;

      .file-name {
        font-weight: 500;
        color: var(--color-61);
        font-size: 14px;
      }

      .file-size {
        color: var(--color-66);
        font-size: 12px;
      }
    }
  }
}
</style>

